// AgentInterviewStage — ElevenLabs Conversational AI agent.
//
// Architecture:
//   browser  ←─ WebSocket ─→  ElevenLabs agent (speech-to-speech + Gemini LLM)
//                                       ↓
//                              POST /agent/finish → InterviewReport
//
// The browser hits our server's POST /agent/start to:
//   - ensure an agent exists on ElevenLabs for the requested round
//   - fetch a signed WebSocket URL + dynamic variables (candidate name, JD,
//     resume background)
//   - then connects directly to ElevenLabs via @elevenlabs/client.
//
// State machine:
//   idle → connecting → listening / speaking / thinking → done
//
// On disconnect (agent ended, candidate clicked end, network drop): we POST
// the conversationId to /agent/finish so the server fetches the transcript
// and generates the InterviewReport.

import React, { useEffect, useRef, useState } from "react";
import { Conversation } from "@elevenlabs/client";
import { useProctorGuard, ProctorWarningOverlay } from "../common/ProctorGuard";
import { useProctoringSession } from "../../hooks/useProctoringSession";
import ProctoringPanel from "../common/ProctoringPanel";
import { API_ROOT } from "../../config/base";

const STATE = {
  IDLE: "idle",
  CONNECTING: "connecting",
  LISTENING: "listening",
  SPEAKING: "speaking",
  THINKING: "thinking",
  ENDING: "ending",
  DONE: "done",
};

// In prod, behind nginx, /api/* is proxied to the api-server container so
// relative URLs work. In dev (client on :3000, server on :5055), we have to
// hit the configured REACT_APP_API_URL — strip the trailing /api so the
// fetch path below ("/api/public/agent/start") doesn't end up as
// "/api/api/public/...".
// Without either override, same origin under this app's path (origin +
// '/hiring'), so the calls go to /hiring/api/... .
const _envBase = process.env.REACT_APP_API_URL || "";
const API_BASE = (typeof window !== "undefined" && window.__API_BASE__) ||
                 (_envBase ? _envBase.replace(/\/api\/?$/, "") : window.location.origin + API_ROOT);

export default function AgentInterviewStage({
  round,
  jobId,
  resumeId,
  stageId = "",          // Enhancement #1: configured-pipeline stage this run belongs to
  candidateName = "there",
  onComplete,
}) {
  const [state, setState]     = useState(STATE.IDLE);
  const [error, setError]     = useState("");
  const [endedReason, setEndedReason] = useState("");
  const conversationRef       = useRef(null);
  const conversationIdRef     = useRef(null);
  const startTimeRef          = useRef(0);
  const finishedRef           = useRef(false);
  const videoRef              = useRef(null);

  // Suppress the "Cannot close a closed AudioContext" error that ElevenLabs
  // client library throws internally during cleanup — it's a known library bug
  // and doesn't affect functionality.
  useEffect(() => {
    const handler = (event) => {
      if (event.message?.includes("Cannot close a closed AudioContext") ||
          event.error?.message?.includes("Cannot close a closed AudioContext")) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };
    window.addEventListener("error", handler);
    window.addEventListener("unhandledrejection", handler);
    return () => {
      window.removeEventListener("error", handler);
      window.removeEventListener("unhandledrejection", handler);
    };
  }, []);

  const isActive = [STATE.CONNECTING, STATE.LISTENING, STATE.SPEAKING, STATE.THINKING].includes(state);

  // ── Comprehensive proctoring session ──────────────────────────────
  const { violations, integrityScore, riskLevel, proctorData } = useProctoringSession({
    active: isActive,
    videoRef,
  });

  // ── Proctoring ─────────────────────────────────────────────────────
  const { warnings, showWarning, warningMsg, dismissWarning, triggerViolation } = useProctorGuard({
    active: isActive,
    onViolation: (reason, count) => {
      console.warn(`[proctor] violation ${count}: ${reason}`);
    },
    onEnd: (reason) => {
      finishCall(reason);
    },
  });

  // ── Mic permission ─────────────────────────────────────────────────
  async function requestMic() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Release immediately — @elevenlabs/client will reacquire
      stream.getTracks().forEach((t) => t.stop());
      return true;
    } catch (e) {
      setError("Microphone access is required for the interview. " + (e.message || ""));
      return false;
    }
  }

  // ── Start ──────────────────────────────────────────────────────────
  async function begin() {
    setError("");
    const ok = await requestMic();
    if (!ok) return;

    setState(STATE.CONNECTING);
    try {
      // Mint a signed URL + context from our server
      const r = await fetch(`${API_BASE}/api/public/agent/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ round, jobId, candidateId: resumeId, stageId }),
      });
      const startData = await r.json();
      if (r.status === 403 && startData.alreadyCompleted) {
        setError("You have already completed this interview. Each round can only be attempted once.");
        setState(STATE.DONE);
        finishedRef.current = true;
        return;
      }
      if (r.status === 503 && startData.missingKey) {
        setError("Voice interviews are not available in this environment — the ElevenLabs API key has not been configured. Please contact your administrator.");
        setState(STATE.IDLE);
        return;
      }
      if (!r.ok) throw new Error(startData.message || "Failed to start agent session");

      startTimeRef.current = Date.now();

      // Connect to ElevenLabs Conversational AI directly
      const conversation = await Conversation.startSession({
        signedUrl:        startData.signedUrl,
        dynamicVariables: startData.dynamicVariables,

        onConnect: ({ conversationId }) => {
          conversationIdRef.current = conversationId;
          setState(STATE.LISTENING);
        },
        onDisconnect: () => {
          if (!finishedRef.current) finishCall("disconnect");
        },
        onError: (e) => {
          console.error("agent error:", e);
          setError(typeof e === "string" ? e : (e?.message || "Agent error"));
        },
        onModeChange: ({ mode }) => {
          // mode: "speaking" (agent is talking) | "listening" (waiting for user)
          if (mode === "speaking") setState(STATE.SPEAKING);
          else if (mode === "listening") setState(STATE.LISTENING);
        },
        onStatusChange: ({ status }) => {
          // status: "connecting" | "connected" | "disconnected"
          // Only end if we were previously connected — ignore initial "disconnected" state
          if (status === "disconnected" && !finishedRef.current && conversationIdRef.current) {
            finishCall("status_disconnect");
          }
        },
      });

      conversationRef.current = conversation;
    } catch (e) {
      console.error("agent/start failed:", e);
      setError(e.message || "Could not start the agent");
      setState(STATE.IDLE);
    }
  }

  // ── End ────────────────────────────────────────────────────────────
  async function finishCall(reason = "user_end") {
    if (finishedRef.current) return;
    finishedRef.current = true;
    setState(STATE.ENDING);
    setEndedReason(reason);

    try {
      if (conversationRef.current) {
        await conversationRef.current.endSession();
      }
    } catch (e) { /* already ended */ }

    const durationSec = (Date.now() - startTimeRef.current) / 1000;

    // Tell our server to grab the transcript + generate the report
    if (conversationIdRef.current) {
      try {
        const r = await fetch(`${API_BASE}/api/public/agent/finish`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            conversationId: conversationIdRef.current,
            round, jobId, candidateId: resumeId, stageId,
            durationSec,
            proctoring: proctorData(),
          }),
        });
        const data = await r.json();
        setState(STATE.DONE);
        onComplete?.(data);
      } catch (e) {
        console.error("agent/finish failed:", e);
        setState(STATE.DONE);
        onComplete?.({ error: e.message });
      }
    } else {
      setState(STATE.DONE);
      onComplete?.({ aborted: true });
    }
  }

  // Cleanup on unmount — only end session if still active (avoids
  // "Cannot close a closed AudioContext" when the agent already ended naturally)
  useEffect(() => () => {
    try {
      if (conversationRef.current && !finishedRef.current) {
        conversationRef.current?.endSession?.();
      }
    } catch {}
  }, []);

  // Tab-switch lockdown handled by useProctorGuard above.

  // ── UI ─────────────────────────────────────────────────────────────
  const stateText = {
    [STATE.IDLE]:       "Click Start when you're ready",
    [STATE.CONNECTING]: "Connecting…",
    [STATE.SPEAKING]:   "Interviewer is speaking…",
    [STATE.LISTENING]:  "Listening — speak now",
    [STATE.THINKING]:   "Thinking…",
    [STATE.ENDING]:     "Wrapping up…",
    [STATE.DONE]:       "Interview complete",
  }[state];

  const ringClass =
    state === STATE.SPEAKING   ? "ring-blue-500 animate-pulse" :
    state === STATE.LISTENING  ? "ring-emerald-500 animate-pulse" :
    state === STATE.THINKING   ? "ring-amber-500" :
    state === STATE.CONNECTING ? "ring-amber-400 animate-pulse" :
    state === STATE.ENDING     ? "ring-slate-400" :
                                 "ring-slate-300";

  const emoji =
    state === STATE.SPEAKING ? "🗣️" :
    state === STATE.LISTENING ? "🎙️" :
    state === STATE.THINKING ? "🧠" :
    state === STATE.CONNECTING ? "⚡" :
    state === STATE.ENDING ? "✓" :
    state === STATE.DONE ? "✅" : "👋";

  const canEnd = [STATE.LISTENING, STATE.SPEAKING, STATE.THINKING].includes(state);

  return (
    <>
    <ProctorWarningOverlay
      show={showWarning} message={warningMsg}
      warnings={warnings} maxWarnings={3}
      onDismiss={dismissWarning}
    />
    <ProctoringPanel
      videoRef={videoRef}
      integrityScore={integrityScore}
      riskLevel={riskLevel}
      violations={violations}
      active={isActive}
    />
    <div className="flex flex-col items-center justify-center p-8 min-h-[60vh] bg-gradient-to-b from-slate-50 to-white rounded-2xl">
      <div className={`w-44 h-44 rounded-full bg-white shadow-xl ring-8 ${ringClass} flex items-center justify-center transition-all`}>
        <div className="text-5xl">{emoji}</div>
      </div>
      <div className="mt-6 text-lg font-medium text-slate-700">{stateText}</div>
      {error && <div className="mt-3 text-sm text-rose-600 max-w-md text-center">{error}</div>}

      {state === STATE.IDLE && (
        <button
          onClick={begin}
          className="mt-6 px-6 py-3 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 shadow"
        >
          Start interview
        </button>
      )}

      {canEnd && (
        <button
          onClick={() => finishCall("user_end")}
          className="mt-6 px-4 py-2 rounded-full bg-slate-200 text-slate-700 hover:bg-slate-300 text-sm"
        >
          End interview
        </button>
      )}

      {state === STATE.DONE && (
        <div className="mt-4 text-sm text-slate-500">
          Thanks — your responses have been recorded. {endedReason && `(${endedReason})`}
        </div>
      )}
    </div>
    </>
  );
}
